// The "Made with jammable.com" badge, in RaceCut's watermark style
// (lib/racecut/watermark.ts): a white rounded box with a black border and a
// hard offset shadow, the mark, a small grey line and the bold domain.
// Drawn once on the main thread, where the site's font and the logo are,
// and stamped on every frame by drawFrame.

const loadImage = (src: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`image ${src}`));
    img.src = src;
  });

export async function buildWatermark(
  logoSrc: string,
): Promise<ImageBitmap | null> {
  if (typeof document === "undefined") return null;
  try {
    const family =
      getComputedStyle(document.body).fontFamily || "system-ui, sans-serif";
    await document.fonts.load(`900 40px ${family}`).catch(() => undefined);
    const logo = await loadImage(logoSrc);

    // Geometry at 1x for a 1080-wide frame, supersampled for a crisp scale-down.
    const S = 3;
    const h = 84,
      pad = 16,
      gap = 14,
      mark = 60,
      fs = 32,
      sub = 22,
      border = 4,
      shadow = 6;
    const measure = document.createElement("canvas").getContext("2d")!;
    measure.font = `900 ${fs}px ${family}`;
    const tw1 = measure.measureText("jammable.com").width;
    measure.font = `700 ${sub}px ${family}`;
    const tw = Math.max(tw1, measure.measureText("MADE WITH").width);
    const w = Math.ceil(pad + mark + gap + tw + pad + 6);
    const canvas = document.createElement("canvas");
    canvas.width = Math.ceil((w + shadow) * S);
    canvas.height = Math.ceil((h + shadow) * S);
    const ctx = canvas.getContext("2d")!;
    ctx.scale(S, S);
    const box = (x: number, y: number) => {
      ctx.beginPath();
      ctx.roundRect(x, y, w, h, h * 0.28);
      ctx.closePath();
    };
    ctx.fillStyle = "#0A0A0A";
    box(shadow, shadow);
    ctx.fill();
    ctx.fillStyle = "#FFFFFF";
    box(0, 0);
    ctx.fill();
    ctx.lineWidth = border;
    ctx.strokeStyle = "#0A0A0A";
    ctx.stroke();
    const lw = (mark * logo.width) / logo.height;
    ctx.drawImage(logo, pad + (mark - lw) / 2, (h - mark) / 2, lw, mark);
    const tx = pad + mark + gap;
    ctx.textBaseline = "middle";
    ctx.textAlign = "left";
    ctx.fillStyle = "#6B6B70";
    ctx.font = `700 ${sub}px ${family}`;
    ctx.fillText("MADE WITH", tx, h / 2 - 17);
    ctx.fillStyle = "#0A0A0A";
    ctx.font = `900 ${fs}px ${family}`;
    ctx.fillText("jammable.com", tx, h / 2 + 15);
    return await createImageBitmap(canvas);
  } catch {
    return null;
  }
}

/** Where RaceCut stamps it: centred, 38% of the width, 87.5% of the way down. */
export function drawWatermark(
  ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
  W: number,
  H: number,
  badge: ImageBitmap,
) {
  const width = Math.round(Math.min(W, H * 0.5625) * 0.38);
  const height = Math.round(badge.height * (width / badge.width));
  ctx.drawImage(
    badge,
    Math.round((W - width) / 2),
    Math.round(Math.min(H * 0.875, H * 0.96 - height)),
    width,
    height,
  );
}
